/**
 * Feed cache — persist the latest successful card deck to localStorage so the
 * app opens instantly on next launch and keeps rendering the last-known good
 * deck when the network is down (airplane mode, flaky metro, bad Wi-Fi).
 *
 * Design notes
 * ------------
 *   • Primary key:  `distill_cached_cards`
 *     Value shape:  `{ v: 1, filters: { "<category>:<mode>": { cards: [...], cachedAt: <ms> } } }`
 *     Keeping every (category, mode) combo under a single root key is important
 *     for two reasons: (a) the product spec names this exact key, and (b) a
 *     single read/write touches localStorage once per operation (localStorage
 *     is synchronous and expensive on large JSON blobs).
 *
 *   • Scope: default mode ONLY. Revision Blitz is a one-shot weighted deck and
 *     Read-History is a server-filtered view — caching either would surface
 *     stale/duplicate cards that mislead the reader. We silently no-op for
 *     those modes rather than erroring, so callers can treat the cache as
 *     write-through.
 *
 *   • Size cap: 60 cards per filter. Big enough to cover ~4 BATCH_SIZE pages
 *     while staying well under the ~5MB localStorage quota even with heavy
 *     card payloads.
 *
 *   • TTL: 7 days. Caches older than that are treated as empty on read so a
 *     returning user doesn't see week-old "fresh" headlines. (The banner UI
 *     separately prefers calling it "saved notes", not "fresh news", so this
 *     is purely a safety net.)
 */

const CACHE_KEY = "distill_cached_cards";
const CACHE_VERSION = 1;
const MAX_CARDS_PER_FILTER = 60;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Only the default feed is cached. Revision/history modes bypass the cache.
 */
export function isCacheableMode(mode) {
  return mode === "default";
}

function filterKey(category, mode) {
  return `${category || "all"}:${mode || "default"}`;
}

function readRoot() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CACHE_VERSION || typeof parsed.filters !== "object") {
      return null;
    }
    return parsed;
  } catch (err) {
    // Corrupt JSON or storage disabled (private tabs on some iOS versions).
    // Fail silently — the feed will reload from the network instead.
    void err;
    return null;
  }
}

function writeRoot(root) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(root));
  } catch (err) {
    // Quota exceeded on a long-running session, user disabled storage, etc.
    // We prefer a working app over a logged warning here.
    void err;
  }
}

/**
 * Return the cached deck for (category, mode) or `null` if there is no
 * cache, it's stale, it's a non-cacheable mode, or storage is unavailable.
 */
export function getCachedCards(category, mode) {
  if (!isCacheableMode(mode)) return null;
  const root = readRoot();
  if (!root) return null;
  const entry = root.filters[filterKey(category, mode)];
  if (!entry || !Array.isArray(entry.cards) || entry.cards.length === 0) return null;
  if (typeof entry.cachedAt !== "number") return null;
  if (Date.now() - entry.cachedAt > CACHE_TTL_MS) return null;
  return { cards: entry.cards, cachedAt: entry.cachedAt };
}

/**
 * Persist a successful fetch. No-ops in non-cacheable modes or when
 * `cards` is empty-array (we don't want an empty cache to shadow a real
 * cache from the previous session).
 */
export function setCachedCards(category, mode, cards) {
  if (!isCacheableMode(mode)) return;
  if (!Array.isArray(cards) || cards.length === 0) return;
  const root = readRoot() || { v: CACHE_VERSION, filters: {} };
  if (root.v !== CACHE_VERSION) {
    root.v = CACHE_VERSION;
    root.filters = {};
  }
  root.filters[filterKey(category, mode)] = {
    cards: cards.slice(0, MAX_CARDS_PER_FILTER),
    cachedAt: Date.now(),
  };
  writeRoot(root);
}

/**
 * Clear every cached filter. Exposed so callers (e.g. a future "sign out"
 * flow) can purge the cache without touching other localStorage keys.
 */
export function clearCachedCards() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (err) {
    void err;
  }
}
